import b58 from 'bs58';
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { PublicKey } from '@solana/web3.js';
import { useAnchorWallet, useConnection, useWallet } from '@solana/wallet-adapter-react';
import { getIdentity, guestIdentity, resetStorageIdentity, setStorageIdentity } from '../services/IdentityService';
import ApiService from '../services/ApiService';
import { AnchorProvider, Idl, Program, Wallet } from '@coral-xyz/anchor';
import idl from '../shared/wagers.json';

interface IdentityState {
    identity: DarknetIdentity,
    authenticated: boolean,
    authenticating: boolean,
    authenticate: (publicKey: PublicKey) => void,
    resetIdentity: () => void,
    program: Program | null
}

export const IdentityContext = createContext<IdentityState>({
    identity: guestIdentity,
    authenticated: false,
    authenticating: false,
    authenticate: () => { },
    resetIdentity: () => { },
    program: null
});

export const IdentityProvider = (props: { children?: React.ReactNode }) => {
    const [program, setProgram] = useState(null);
    const { connection } = useConnection();
    const wallet = useAnchorWallet();
    const { publicKey, signMessage, connected, disconnecting } = useWallet();
    const identityRef = useRef<DarknetIdentity>(guestIdentity);
    const [authenticated, setAuthenticated] = useState(false);
    const [authenticating, setAuthenticating] = useState(false);

    const setIdentity = useCallback((identity: DarknetIdentity) => {
        identityRef.current = identity;
        setStorageIdentity(identity);
        setAuthenticated(true);
        setAuthenticating(false);

        console.log('wallet on identity provider', wallet);
        const provider = new AnchorProvider(connection, wallet! as Wallet, { commitment: 'confirmed' });
        // @ts-ignore
        setProgram(new Program(idl as Idl, "WAgeRAWvaBbESYqCVU57Wr4TtnnTfcT3CaP1gZh8qPv", provider));
    }, []);

    const resetIdentity = () => {
        identityRef.current = guestIdentity
        resetStorageIdentity();
        setAuthenticated(false);
    }

    const authenticate = useCallback(async (publicKey: PublicKey) => {
        if (!signMessage) return;

        const message = `By clicking "Approve" below, you agree that you are the valid holder and owner of the connecting wallet and authorize Darknet Games to use this information to authenticate and access the associated account.`;
        const encodedMessage = new TextEncoder().encode(message);
        const signedMessage = await signMessage(encodedMessage);

        setAuthenticating(true);
        ApiService.post('/authenticate', {
            encodedMessage: b58.encode(encodedMessage),
            signedMessage: b58.encode(signedMessage),
            publicKey: b58.encode(publicKey.toBytes())
        }).then((response: CallbackResponse) => {
            if (response.success) {
                setIdentity(response.data);
            } else {
                console.log(response.message);
            }
        })
    }, [setIdentity, signMessage]);

    const validate = useCallback(async (identity: DarknetIdentity) => {
        ApiService.post('/validate', {
            token: identity.token
        }).then((response: CallbackResponse) => {
            if (response.success) {
                setIdentity(identity);
            } else {
                resetIdentity();
                console.error(response.message);
            }
        })
    }, []);

    useEffect(() => {
        if (!authenticated) {
            getIdentity().then((identity) => {
                if (!identity.token) {
                    if (publicKey && connected && !disconnecting) {

                        authenticate(publicKey);
                    }
                } else {
                    validate(identity);
                }
            });
        }
    }, [authenticate, authenticated, connected, disconnecting, publicKey]);

    useEffect(() => {
        if (disconnecting) {
            resetIdentity();
        }
    }, [disconnecting]);

    return (
        <IdentityContext.Provider value={{ identity: identityRef.current, authenticated, authenticating, authenticate, resetIdentity, program }}>
            {props.children}
        </IdentityContext.Provider>
    )
}

export const useDarknetIdentity = () => useContext(IdentityContext);